/**
 * GroundTruth Bulk Editor — the chat's cards as AgentSidebar feed items.
 *
 * Shared by the two GroundTruth bulk-editor surfaces (the Campaign Launch
 * Workflow review pane and the launched-campaign details page) so they cannot
 * drift. Every other platform keeps the single live-card feed its host builds.
 *
 * What it renders, all in chronological position:
 *
 *  · EVERY proposal card, not just the live one. A confirmed proposal used to
 *    vanish, replaced by a one-line "Applied" note; now its card stays where it
 *    was proposed and says what became of it. Only the latest pending proposal
 *    carries Confirm / Cancel; older cards are read-only with their row details
 *    folded (one click away).
 *  · The agent's choice cards, each as its own message under the explanation
 *    that led to it. Only the latest open one is clickable.
 *  · A note for a typed reply that did not resolve to one option, with the
 *    current choices redisplayed under it.
 *
 * ── Where a card sits ───────────────────────────────────────────────────────
 * A card arrives on the TOOL RESULT, before the agent finishes the sentence
 * explaining it, so its arrival time would put it above its own explanation.
 * Instead it is anchored to the end of the stretch it belongs to. The card is
 * "released" by the agent's first message after it arrived (the explanation);
 * it sits just before the first user message after that, or at the moment it
 * stopped awaiting the user (confirmed, cancelled, replaced, answered),
 * whichever came first. A card still awaiting the user with nothing after it
 * sits at the end. So the reading order is always: the agent explains → the
 * card → what the user did → what happened next — including when the user
 * typed something while the agent was still explaining.
 */

import { itemReadyToShow } from './previewGate';
import { latestOpenChoiceSet, latestPendingProposal } from './gtChatState';
import BulkEditPanel from './BulkEditPanel';
import ChoiceCard from './ChoiceCard';

/** Just before the anchoring message, and after anything that came before it. */
const EPSILON = 0.001;

/**
 * Where a card sits in the feed.
 *
 * @param {Array}  messages   session.messages ({ role, ts })
 * @param {number} since      when the card appeared
 * @param {number} decidedAt  when it stopped awaiting the user, if it has
 * @returns {{ ts: number } | { pin: 'end' }} a feedOrder position
 */
export function anchorPosition(messages, since, decidedAt = null) {
  if (!Number.isFinite(since)) return { pin: 'end' };
  const list = Array.isArray(messages) ? messages : [];
  // Released by the agent's first message since it arrived — its explanation.
  let release = since;
  let firstAgent = Infinity;
  for (const m of list) {
    if (m?.role === 'agent' && Number.isFinite(m.ts) && m.ts >= since && m.ts < firstAgent) firstAgent = m.ts;
  }
  // An agent message after the card was decided belongs to what came next
  // (the reply to the pick, the wrap-up after Confirm) — not to its explanation.
  if (Number.isFinite(decidedAt) && firstAgent >= decidedAt) firstAgent = Infinity;
  if (firstAgent !== Infinity) release = firstAgent;
  let bound = Number.isFinite(decidedAt) ? decidedAt : Infinity;
  for (const m of list) {
    if (m?.role === 'user' && Number.isFinite(m.ts) && m.ts > release && m.ts < bound) bound = m.ts;
  }
  // Every bound is later than the release (a user message after it, or a
  // decision after the explanation — an explanation stamped at/after the
  // decision was discarded above), so the card never sorts above it.
  return bound === Infinity ? { pin: 'end' } : { ts: bound - EPSILON };
}

/**
 * @param {object} agent  the useBulkEditorSession result (GroundTruth)
 * @param {object} opts
 * @param {Function} opts.onConfirm  (previewId) => void — the live card's Confirm
 * @param {Function} opts.onDismiss  (previewId) => void — the live card's Cancel
 * @param {boolean}  opts.savesImmediately  the surface auto-saves on confirm
 * @param {string}   opts.roleLabel  the agent's name on its messages
 * @returns {Array} AgentSidebar feedItems ({ key, ts | pin, node })
 */
export function buildGtBulkFeedItems(agent, {
  onConfirm,
  onDismiss,
  savesImmediately = true,
  roleLabel = 'Bulk editor',
} = {}) {
  if (!agent?.gtChat) return [];
  const messages = agent.messages || [];
  const proposals = Array.isArray(agent.proposals) ? agent.proposals : [];
  const choiceSets = Array.isArray(agent.choiceSets) ? agent.choiceSets : [];
  const notices = Array.isArray(agent.choiceNotices) ? agent.choiceNotices : [];
  const livePending = latestPendingProposal(agent);
  const openSet = latestOpenChoiceSet(agent);
  const newestProposalId = proposals.length ? proposals[proposals.length - 1].previewId : null;
  const onChoose = (choiceSetId, choiceId) => agent.choose?.(choiceSetId, choiceId, 'click');
  const items = [];

  for (const p of proposals) {
    const live = p.status === 'pending' && livePending?.previewId === p.previewId;
    // The live card waits for the agent's explanation (or a settled turn), as
    // the classic card always has. History is shown as soon as it exists.
    if (live && !p.restored && !itemReadyToShow(agent, p.ts)) continue;
    // A pending card that is not the latest cannot be confirmed; never show it
    // with live controls.
    const status = live ? 'pending' : (p.status === 'pending' ? 'superseded' : p.status);
    items.push({
      key: `gt-proposal-${p.previewId}`,
      order: Number.isFinite(p.ts) ? p.ts : Infinity,
      ...anchorPosition(messages, p.ts, live ? null : p.decidedAt),
      node: (
        <BulkEditPanel
          preview={p.preview}
          status={status}
          statusDetail={live ? null : p.detail}
          defaultCollapsed={p.previewId !== newestProposalId}
          onConfirm={live ? onConfirm : undefined}
          onDismiss={live ? onDismiss : undefined}
          approving={live ? Boolean(agent.approving) : false}
          approveError={live ? agent.approveError : null}
          savesImmediately={savesImmediately}
        />
      ),
    });
  }

  for (const set of choiceSets) {
    const isOpen = set.status === 'open' && openSet?.choice_set_id === set.choice_set_id;
    if (isOpen && !set.restored && !itemReadyToShow(agent, set.ts)) continue;
    const shown = isOpen || set.status !== 'open' ? set : { ...set, status: 'superseded' };
    items.push({
      key: `gt-choices-${set.choice_set_id}`,
      order: Number.isFinite(set.ts) ? set.ts : Infinity,
      ...anchorPosition(messages, set.ts, isOpen ? null : set.decidedAt),
      node: (
        <div className="clw-ai-msg agent clw-choice-msg">
          <div className="clw-ai-msg-role">{roleLabel}</div>
          <ChoiceCard
            choiceSet={shown}
            onChoose={isOpen ? onChoose : undefined}
            busy={isOpen && agent.choosing === set.choice_set_id}
            error={isOpen ? agent.choiceError : null}
          />
        </div>
      ),
    });
  }

  const latestNotice = notices[notices.length - 1] || null;
  for (const n of notices) {
    // The choices are redisplayed under the NEWEST note only, and only while
    // that question is still the one being asked.
    const redisplay = n === latestNotice && openSet && openSet.choice_set_id === n.choiceSetId;
    items.push({
      key: n.key,
      order: n.ts,
      ts: n.ts,
      node: (
        <div className="clw-ai-msg agent clw-choice-msg">
          <div className="clw-ai-msg-role">{roleLabel}</div>
          <div className="clw-ai-msg-bubble clw-choice-notice" role="status">{n.reason}</div>
          {redisplay && (
            <ChoiceCard
              choiceSet={openSet}
              onChoose={onChoose}
              busy={agent.choosing === openSet.choice_set_id}
              error={agent.choiceError}
              redisplay
            />
          )}
        </div>
      ),
    });
  }

  // Creation order breaks ties between cards anchored to the same moment.
  return items
    .sort((a, b) => (a.order === b.order ? 0 : (a.order < b.order ? -1 : 1)))
    .map(({ order, ...item }) => item);
}

export default buildGtBulkFeedItems;
